export const N = 8;

export function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const key = (y, x) => y * N + x;
export const inB = (y, x) => y >= 0 && y < N && x >= 0 && x < N;
const same = (a, b) => a && b && a.c >= 0 && a.c === b.c;

export function pickColor(g, y, x, colors, rand) {
  for (let i = 0; i < 20; i++) {
    const c = Math.floor(rand() * colors);
    const l2 = x >= 2 && g[y][x - 1]?.c === c && g[y][x - 2]?.c === c;
    const u2 = y >= 2 && g[y - 1]?.[x]?.c === c && g[y - 2]?.[x]?.c === c;
    if (!l2 && !u2) return c;
  }
  return Math.floor(rand() * colors);
}

export function findRuns(g) {
  const runs = [];
  for (let y = 0; y < N; y++) {
    let x = 0;
    while (x < N) {
      let e = x + 1;
      while (e < N && same(g[y][x], g[y][e])) e++;
      if (e - x >= 3) runs.push({ dir: "h", cells: Array.from({ length: e - x }, (_, i) => [y, x + i]) });
      x = e;
    }
  }
  for (let x = 0; x < N; x++) {
    let y = 0;
    while (y < N) {
      let e = y + 1;
      while (e < N && same(g[y][x], g[e][x])) e++;
      if (e - y >= 3) runs.push({ dir: "v", cells: Array.from({ length: e - y }, (_, i) => [y + i, x]) });
      y = e;
    }
  }
  return runs;
}

export function groups(g, prefer = []) {
  const runs = findRuns(g);
  const out = [];
  const used = new Array(runs.length).fill(false);
  for (let i = 0; i < runs.length; i++) {
    if (used[i]) continue;
    const grp = { runs: [runs[i]], cells: new Map(runs[i].cells.map(([y, x]) => [key(y, x), [y, x]])) };
    used[i] = true;
    let grew = true;
    while (grew) {
      grew = false;
      for (let j = 0; j < runs.length; j++) {
        if (used[j] || !runs[j].cells.some(([y, x]) => grp.cells.has(key(y, x)))) continue;
        used[j] = true; grew = true; grp.runs.push(runs[j]);
        runs[j].cells.forEach(([y, x]) => grp.cells.set(key(y, x), [y, x]));
      }
    }
    const longest = grp.runs.reduce((a, b) => (b.cells.length > a.cells.length ? b : a));
    const hasH = grp.runs.some((r) => r.dir === "h"), hasV = grp.runs.some((r) => r.dir === "v");
    const len = longest.cells.length;
    grp.special = len >= 5 ? "b" : hasH && hasV ? "w" : len === 4 ? (longest.dir === "h" ? "v" : "h") : null;
    const pref = prefer.find(([y, x]) => grp.cells.has(key(y, x)));
    grp.at = pref || longest.cells[Math.floor(len / 2)];
    const [ay, ax] = grp.at;
    grp.c = g[ay][ax].c;
    out.push(grp);
  }
  return out;
}

export function blastCells(g, y, x, sp, colorHint) {
  const cells = [];
  if (sp === "h") for (let i = 0; i < N; i++) cells.push([y, i]);
  else if (sp === "v") for (let i = 0; i < N; i++) cells.push([i, x]);
  else if (sp === "w") for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) inB(y + dy, x + dx) && cells.push([y + dy, x + dx]);
  else if (sp === "b") {
    const c = colorHint ?? dominantColor(g);
    for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) g[i][j]?.c === c && cells.push([i, j]);
  }
  return cells;
}

export function dominantColor(g) {
  const cnt = {};
  g.flat().forEach((t) => t && t.c >= 0 && (cnt[t.c] = (cnt[t.c] || 0) + 1));
  return Number(Object.entries(cnt).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0);
}

const swapG = (g, a, b) => { const t = g[a[0]][a[1]]; g[a[0]][a[1]] = g[b[0]][b[1]]; g[b[0]][b[1]] = t; };

export function swapValid(g, a, b) {
  const A = g[a[0]][a[1]], B = g[b[0]][b[1]];
  if (!A || !B) return false;
  if (A.sp === "b" || B.sp === "b" || (A.sp && B.sp)) return true;
  swapG(g, a, b);
  const ok = findRuns(g).some((r) => r.cells.some(([y, x]) => (y === a[0] && x === a[1]) || (y === b[0] && x === b[1])));
  swapG(g, a, b);
  return ok;
}

export function findHint(g) {
  const opts = [];
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    if (x < N - 1 && swapValid(g, [y, x], [y, x + 1])) opts.push([[y, x], [y, x + 1]]);
    if (y < N - 1 && swapValid(g, [y, x], [y + 1, x])) opts.push([[y, x], [y + 1, x]]);
  }
  if (!opts.length) return null;
  const sp = opts.find(([a, b]) => g[a[0]][a[1]].sp || g[b[0]][b[1]].sp);
  return sp || opts[Math.floor(Math.random() * opts.length)];
}

export function reshuffle(g, rand) {
  const tiles = g.flat().filter((t) => t && !t.sp);
  for (let tries = 0; tries < 60; tries++) {
    const cs = tiles.map((t) => t.c);
    for (let i = cs.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [cs[i], cs[j]] = [cs[j], cs[i]]; }
    tiles.forEach((t, i) => { t.c = cs[i]; });
    if (!findRuns(g).length && findHint(g)) return true;
  }
  return false;
}

export { swapG };
